import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Download, Eye, KeyRound } from 'lucide-react';
import { DropZone } from '@/components/DropZone';
import { formatDateTime } from '@/components/live';
import { callRpc } from '@/api/resources';
import { signedUrlsFor, uploadDocument } from '@/api/documents';
import { OTP_FAILURES, sendOtp, verifyOtp, type IssuedOtp } from '@/api/otp';
import { useAuth } from '@/auth/AuthProvider';
import { languageName } from '@/lib/languages';
import { maskMiddle } from '@/lib/plotConsentDocument';
import { toast } from '@/lib/toast';

export interface ConsentDocument {
  link_id: string; link_type: string; linked_at: string; document_id: string; original_filename: string | null;
  mime_type: string | null; sha256_hash?: string | null;
}

/** A copy's language, from its file name: "plot-consent-PLT-000001-kn-signed.pdf" -> 'kn'. */
export function documentLanguage(filename: string | null | undefined): string | null {
  return /-([a-z]{2})(?:-signed|-paper|-for-signature)?\.pdf$/.exec(filename ?? '')?.[1] ?? null;
}

/**
 * The consent's documents, one row per language (owner, 2026-09-28: "this section will have english
 * and kannada. so 2 drafts generated. update as view, download draft, download consented copy
 * (disabled in draft state). once digital consent is obtained, it gets autoplaced. drag and drop for
 * rows / upload for Print (signed paper). add document hash key, OTP key masked"):
 *   - the draft of each language: view, download;
 *   - the consented copy of each language: download once consent is given - the digital one is placed
 *     here when the OTP is verified; a signed paper copy is dropped onto its row;
 *   - each copy's SHA-256 and the OTP reference, masked; the staff member's own OTP reveals them.
 */
export function ConsentCopies({ consentId, plotCode, languages, documents, otpReference, given, mayUpload, filePrefix = 'plot-consent', printCopy = null }: {
  consentId: string; plotCode: string; filePrefix?: string;
  /** The print copy to sign by hand, made with the draft (owner, 2026-09-28: "both digital and print as standard"). */
  printCopy?: ConsentDocument | null; languages: string[]; documents: ConsentDocument[];
  otpReference: string | null; given: boolean; mayUpload: boolean;
}) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [revealed, setRevealed] = useState(false);
  const [issued, setIssued] = useState<IssuedOtp | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const latest = (lang: string, type: 'generated' | 'primary') => documents
    .filter((row) => row.link_type === type && documentLanguage(row.original_filename) === lang)
    .sort((a, b) => b.linked_at.localeCompare(a.linked_at))[0];
  const key = (value: string | null | undefined) => (value ? (revealed ? value : maskMiddle(value)) : '--');

  async function urlOf(row: ConsentDocument) {
    return (await signedUrlsFor([row.document_id]))[row.document_id];
  }
  async function view(row: ConsentDocument) {
    const url = await urlOf(row);
    if (url) window.open(url, '_blank', 'noopener');
  }
  async function download(row: ConsentDocument) {
    const url = await urlOf(row);
    if (!url) return;
    // Fetched and saved under its own name: a cross-origin link ignores the download attribute.
    const blob = await (await fetch(url)).blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = row.original_filename ?? 'consent.pdf';
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 5000);
  }

  /** A signed paper copy for one language, kept as that language's consented copy. */
  async function upload(lang: string, file: File) {
    if (!profile?.organization) return;
    setBusy(`upload-${lang}`);
    try {
      const extension = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : 'pdf';
      const named = new File([file], `${filePrefix}-${plotCode}-${lang === 'en' ? 'en' : `en-${lang}`}-paper.${extension === 'pdf' ? 'pdf' : extension}`, { type: file.type });
      const stored = await uploadDocument({ file: named, bucket: 'generated-documents', organizationId: profile.organization.id, ownerId: consentId, captureMethod: 'upload' });
      await callRpc('fn_link_document', { p_document_id: stored.id, p_entity_table: 'party.party_consents', p_entity_id: consentId, p_link_type: 'primary' });
      await queryClient.invalidateQueries({ queryKey: ['record_documents'] });
      toast.success(given ? 'Signed paper copy kept.' : 'Signed paper copy kept. Record the consent with "Record signed paper".');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The copy could not be kept.');
    } finally {
      setBusy(null);
    }
  }

  /** The staff member's own code: a masked key is shown in full only to someone who proves who they are now. */
  async function askCode() {
    setBusy('reveal');
    try {
      const sent = await sendOtp({ purpose: 'step_up', channel: 'whatsapp' }).catch(() => sendOtp({ purpose: 'step_up', channel: 'sms' }));
      setIssued(sent);
      toast.success(`A code was sent to your ${sent.destination_masked}.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The code could not be sent.');
    } finally {
      setBusy(null);
    }
  }
  async function confirmCode() {
    if (!issued) return;
    setBusy('reveal');
    try {
      const result = await verifyOtp(issued.otp_event_id, code.trim());
      if (!result.verified) { toast.error(OTP_FAILURES[result.failure_reason ?? ''] ?? 'That code is not right.'); return; }
      setRevealed(true);
      setIssued(null);
      setCode('');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="consent-copies">
      <table className="simple-table">
        <thead>
          <tr><th>Languages</th><th>Draft</th><th>Print copy</th><th>Consented copy</th><th>Keys</th></tr>
        </thead>
        <tbody>
          {languages.map((lang) => {
            const draft = latest(lang, 'generated');
            const consented = latest(lang, 'primary');
            return (
              <tr key={lang}>
                <td><strong>{lang === 'en' ? 'English' : `English + ${languageName(lang).replace(/^[A-Z]{2} - /, '')}`}</strong></td>
                <td>
                  {draft ? (
                    <span className="cell-stack">
                      <span className="icon-text">
                        <button onClick={() => void view(draft)} type="button"><Eye size={14} /> View</button>
                        <button onClick={() => void download(draft)} type="button"><Download size={14} /> Download draft</button>
                      </span>
                      <small className="muted">Generated {formatDateTime(draft.linked_at)}</small>
                    </span>
                  ) : <span className="muted">Not generated yet</span>}
                </td>
                <td>
                  {printCopy ? (
                    <span className="cell-stack">
                      <span className="icon-text">
                        <button onClick={() => void view(printCopy)} type="button"><Eye size={14} /> View</button>
                        <button onClick={() => void download(printCopy)} type="button"><Download size={14} /> Download</button>
                      </span>
                      <small className="muted">Made {formatDateTime(printCopy.linked_at)}</small>
                    </span>
                  ) : <span className="muted">Made with the draft</span>}
                </td>
                <td>
                  <span className="cell-stack">
                    <span className="icon-text">
                      <button disabled={!consented} onClick={() => consented && void view(consented)} type="button"><Eye size={14} /> View</button>
                      <button disabled={!consented} onClick={() => consented && void download(consented)}
                              title={consented ? undefined : 'Available once consent is given'} type="button">
                        <Download size={14} /> Download consented copy
                      </button>
                    </span>
                    {consented
                      ? <small className="muted">{/-paper\./.test(consented.original_filename ?? '') ? 'Signed paper' : 'Digital (OTP)'} · {formatDateTime(consented.linked_at)}</small>
                      : mayUpload && (
                        <DropZone accept="application/pdf,image/*" buttonLabel={busy === `upload-${lang}` ? 'Uploading...' : 'Upload'}
                                  label="Signed paper copy: drop here, or" onFile={(file) => void upload(lang, file)} />
                      )}
                  </span>
                </td>
                <td>
                  <span className="cell-stack fp">
                    <small>SHA-256 {key(consented?.sha256_hash ?? draft?.sha256_hash)}</small>
                    {lang === languages[0] && <small>OTP {key(otpReference)}</small>}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!revealed && (
        <div className="inline-form-actions">
          {issued ? (
            <>
              <input aria-label="Your code" inputMode="numeric" maxLength={6} onChange={(event) => setCode(event.target.value)} placeholder="000000" value={code} />
              <button className="primary" disabled={busy === 'reveal' || code.trim().length !== 6} onClick={() => void confirmCode()} type="button">Reveal</button>
            </>
          ) : (
            <button disabled={busy === 'reveal'} onClick={() => void askCode()} type="button"><KeyRound size={14} /> Reveal keys with your OTP</button>
          )}
        </div>
      )}
    </section>
  );
}
